// Converts raw streamed assistant text into safe bubble HTML.
//
// Streaming replies (AE-185) are conversational text followed by a JSON
// trailer (see docs/architecture/AE-STREAMING-SPEC.md §4.2). The trailer, and
// any partial prefix of its marker, must never be visible to the user — this
// module strips that protocol text and renders the remainder (Markdown or
// HTML) into bubble-safe HTML.
import markdownit from "markdown-it";

export const JSON_TRAILER_MARKER = "---JSON_TRAILER---";

const md = markdownit({ html: true, linkify: true, typographer: true });

const HTML_TAG_NEAR_START = /^\s*<(p|div|br|ul|ol|h[1-6]|strong|em|span|a|blockquote|table|img|li)\b/i;

// Length of the longest suffix of `text` that is also a proper prefix of
// JSON_TRAILER_MARKER — i.e. how much trailing text might be the start of
// the marker arriving across token boundaries. Withholding this suffix keeps
// the marker from ever flashing on screen mid-stream.
function partialMarkerSuffixLength(text: string): number {
  const maxLen = Math.min(JSON_TRAILER_MARKER.length - 1, text.length);
  for (let len = maxLen; len > 0; len--) {
    if (text.endsWith(JSON_TRAILER_MARKER.slice(0, len))) {
      return len;
    }
  }
  return 0;
}

// Belt-and-suspenders for a missing marker: strip a trailing JSON object
// that looks like the agent_routing trailer even without the marker prefix.
function stripTrailingAgentRoutingJson(text: string): string {
  const blankLineMatch = text.match(/\n\s*\n(\{[\s\S]*)$/);
  if (blankLineMatch) {
    const candidate = blankLineMatch[1];
    if (candidate.slice(0, 60).includes('"agent_routing"')) {
      return text.slice(0, text.length - candidate.length).trimEnd();
    }
  }

  const lastBrace = text.lastIndexOf("{");
  if (lastBrace !== -1) {
    const candidate = text.slice(lastBrace);
    if (/^\{\s*"agent_routing"/.test(candidate)) {
      return text.slice(0, lastBrace).trimEnd();
    }
  }

  return text;
}

/** Strips the JSON trailer (and any partial marker prefix) from raw streamed text. */
export function stripStreamProtocol(raw: string): string {
  if (!raw) return "";

  const markerIndex = raw.indexOf(JSON_TRAILER_MARKER);
  if (markerIndex !== -1) {
    return raw.slice(0, markerIndex).trimEnd();
  }

  const suffixLen = partialMarkerSuffixLength(raw);
  if (suffixLen > 0) {
    return raw.slice(0, raw.length - suffixLen);
  }

  return stripTrailingAgentRoutingJson(raw);
}

/** True when text is already rendered HTML rather than plain Markdown/text. */
export function looksLikeHtml(text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) return false;
  if (!/<\/?[a-z][\s\S]*>/i.test(trimmed)) return false;
  return trimmed.startsWith("<") || HTML_TAG_NEAR_START.test(trimmed);
}

/** Renders raw (possibly still-streaming) assistant text into safe bubble HTML. */
export function toAssistantBubbleHtml(text: string): string {
  if (!text) return "";

  const stripped = stripStreamProtocol(text);
  if (!stripped.trim()) return "";

  if (looksLikeHtml(stripped)) {
    return stripped.replace(/•\s*/g, "<br>");
  }

  return md.render(stripped).replace(/•\s*/g, "<br>");
}
